import React, { useState, useEffect, useRef, Suspense, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import RootRuntimeBridge from './RootRuntimeBridge';
import { clearSecureSession, rootAction, rootApi } from './RootScurityClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Icons from '@fortawesome/free-solid-svg-icons';
import "../styles/Dashboard.css";

// ==============================================
// IMPORT MODULE CONFIGURATIONS
// ==============================================
import { getModuleByRole } from '../module_configs';
import { resolveSpecialMenu } from './SpecialMenuRegistry';

// ==============================================
// INSTANT TRANSLATOR CLASS
// ==============================================
class InstantTranslator {
  constructor() {
    this.cache = new Map();
    this.bundleRequests = new Map();
    this.loadFromCache();
    this.pendingTranslations = new Map();
  }

  loadFromCache() {
    try {
      const cached = localStorage.getItem('ourzest_translations');
      if (cached) {
        const parsed = JSON.parse(cached);
        Object.entries(parsed).forEach(([key, value]) => this.cache.set(key, value));
      }
    } catch (e) {}
  }

  saveToCache() {
    try {
      const cacheObj = {};
      this.cache.forEach((value, key) => { cacheObj[key] = value; });
      localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
    } catch (e) {}
  }

  defaultTexts = {
    dashboard: 'Dashboard',
    welcome: 'Welcome back',
    searchPlaceholder: 'Search menus, submenus...',
    notifications: 'Notifications',
    markAllRead: 'Mark all as read',
    viewAll: 'View All',
    myProfile: 'My Profile',
    settings: 'Settings',
    helpSupport: 'Help & Support',
    logout: 'Logout',
    logoutThisDevice: 'Logout this device',
    loggingOut: 'Logging out…',
    modules: 'MODULES',
    systemOnline: 'System Online',
    switchShops: 'SWITCH SHOP',
    availableShops: 'Available Shops',
    loadingShops: 'Loading shops...',
    noShopsAvailable: 'No shops available',
    refreshShops: 'Refresh Shops',
    role: 'Role',
    close: 'Close',
    loading: 'Loading workspace...',
    themeColor: 'THEME COLOR',
    reorderNavigation: 'Reorder navigation',
    finishOrdering: 'Finish ordering',
    reorderHelp: 'Reorder menus and submenus',
    restoreOrder: 'Restore configured order',
    searchResults: 'SEARCH RESULTS',
    noMatchingMenus: 'No matching menus found',
    keyboardShortcuts: 'Keyboard shortcuts',
    authorizedShortcutsOnly: 'Only shortcuts you are authorized to use are shown.',
    language: 'LANGUAGE',
    selectLanguage: 'Select language',
    loadingAnalytics: 'Loading analytics',
    navigationMenu: 'Navigation Menu',
    menu: 'Menu',
    selectModule: 'Select a module from the navigation to begin.',
    loadingAccess: 'Loading package access…'
  };

  async translateText(text, targetLang) {
    if (targetLang === 'en') return text;
    const cacheKey = `${text}_${targetLang}`;
    if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);
    
    if (!this.pendingTranslations.has(cacheKey)) {
      this.pendingTranslations.set(cacheKey, this._performTranslation(text, targetLang, cacheKey));
    }
    return this.pendingTranslations.get(cacheKey);
  }

  async _performTranslation(text, targetLang, cacheKey) {
    try {
      const response = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`);
      if (response.ok) {
        const data = await response.json();
        const translated = data?.responseData?.translatedText || text;
        this.cache.set(cacheKey, translated);
        this.saveToCache();
        return translated;
      }
    } catch (e) {}
    return text;
  }

  async getTranslations(lang) {
    if (lang === 'en') return this.defaultTexts;
    if (LOCAL_TRANSLATIONS[lang]) {
      return { ...this.defaultTexts, ...LOCAL_TRANSLATIONS[lang] };
    }
    if (this.bundleRequests.has(lang)) return this.bundleRequests.get(lang);

    const request = (async () => {
      const translations = { ...this.defaultTexts };
      const promises = Object.entries(this.defaultTexts).map(async ([key, text]) => {
        translations[key] = await this.translateText(text, lang);
      });
      await Promise.all(promises);
      return translations;
    })();
    this.bundleRequests.set(lang, request);
    try {
      return await request;
    } catch (error) {
      this.bundleRequests.delete(lang);
      throw error;
    }
  }
}

const translator = new InstantTranslator();

const LANGUAGES = {
  en: { name: 'English', nativeName: 'English', icon: '🇬🇧', dir: 'ltr' },
  ur: { name: 'Urdu', nativeName: 'اردو', icon: '🇵🇰', dir: 'rtl' },
};

const LOCAL_TRANSLATIONS = {
  ur: {
    dashboard: 'ڈیش بورڈ', welcome: 'دوبارہ خوش آمدید',
    searchPlaceholder: 'مینیو اور ذیلی مینیو تلاش کریں...', logout: 'لاگ آؤٹ',
    switchShops: 'دکان تبدیل کریں', loadingShops: 'دکانیں لوڈ ہو رہی ہیں...',
    noShopsAvailable: 'کوئی دکان دستیاب نہیں', loading: 'ورک اسپیس لوڈ ہو رہی ہے...',
    themeColor: 'تھیم کا رنگ', close: 'بند کریں', reorderNavigation: 'نیویگیشن کی ترتیب بدلیں',
    finishOrdering: 'ترتیب مکمل کریں', reorderHelp: 'مینیو اور ذیلی مینیو کی ترتیب بدلیں', restoreOrder: 'اصل ترتیب بحال کریں',
    searchResults: 'تلاش کے نتائج', noMatchingMenus: 'کوئی مماثل مینیو نہیں ملا', keyboardShortcuts: 'کی بورڈ شارٹ کٹس',
    authorizedShortcutsOnly: 'صرف وہ شارٹ کٹس دکھائے گئے ہیں جن کی آپ کو اجازت ہے۔', language: 'زبان', selectLanguage: 'زبان منتخب کریں',
    navigationMenu: 'نیویگیشن مینیو', menu: 'مینیو', selectModule: 'شروع کرنے کے لیے نیویگیشن سے ایک ماڈیول منتخب کریں۔', loadingAccess: 'پیکیج رسائی لوڈ ہو رہی ہے…',
    logoutThisDevice: 'اس ڈیوائس سے لاگ آؤٹ', loggingOut: 'لاگ آؤٹ ہو رہا ہے…'
  }
};

const GROUP_TRANSLATIONS = {
  ur: { management: 'انتظام', sales: 'فروخت اور واپسی', inventory: 'اسٹاک اور انوینٹری', accounts: 'اکاؤنٹس اور لین دین', billing: 'بلنگ اور تجدید', settings: 'ترتیبات' },
};

const MENU_TRANSLATIONS = {
  ur: {
    dashboard: 'ڈیش بورڈ', customers: 'گاہک', addcustomer: 'گاہک شامل کریں', customerlist: 'گاہکوں کی فہرست',
    suppliers: 'سپلائرز', addsupplier: 'سپلائر شامل کریں', supplierlist: 'سپلائرز کی فہرست', employees: 'ملازمین',
    addemployees: 'ملازم شامل کریں', employeeslist: 'ملازمین کی فہرست', sales: 'فروخت', addsale: 'نئی فروخت',
    saleslist: 'فروخت کی فہرست', salesproductswise: 'مصنوعات کے لحاظ سے فروخت', returns: 'فروخت کی واپسی',
    addreturn: 'نئی فروخت واپسی', returnlist: 'فروخت واپسی کی فہرست', stock: 'اسٹاک', addstock: 'اسٹاک شامل کریں',
    stocklist: 'اسٹاک کی فہرست', purchases: 'خریداری', addpurchase: 'نئی خریداری', purchaselist: 'خریداری کی فہرست',
    purchasessupplierwise: 'سپلائر کے لحاظ سے', purchasesinvoicewise: 'بل کے لحاظ سے', purchasereturns: 'خریداری کی واپسی',
    addpurchasereturn: 'نئی خریداری واپسی', purchasereturnlist: 'خریداری واپسی کی فہرست', barcode: 'بارکوڈز',
    barcodeprinting: 'بارکوڈ پرنٹنگ', financials: 'مالی امور', newtransaction: 'نیا لین دین', alltransactions: 'تمام لین دین',
    accounts: 'اکاؤنٹس', cashledger: 'نقد اور بینک لیجر', billing: 'بلنگ اور تجدید', generatebill: 'بل بنائیں',
    billinghistory: 'بلنگ کی تاریخ', renewals: 'تجدید', settings: 'ترتیبات', systemupdates: 'اطلاعاتی مرکز',
    partners: 'شراکت دار', addpartners: 'شراکت دار شامل کریں', partnerslist: 'شراکت داروں کی فہرست',
    gatepass: 'گیٹ پاس', addgatepass: 'گیٹ پاس شامل کریں', gatepasslist: 'گیٹ پاس کی فہرست',
    pendinginvoices: 'زیر التوا رسیدیں', pendinginvoicesadd: 'رسید شامل کریں', invoiceslist: 'رسیدوں کی فہرست',
    stocktransfer: 'اسٹاک منتقلی', stockcentralization: 'اسٹاک مرکزیت', warranty: 'وارنٹی',
    addwarranty: 'وارنٹی شامل کریں', warrantylist: 'وارنٹی کی فہرست', users: 'صارفین'
  }
};

const THEME_PRESETS = [
  { name: 'Amber Glow', hex: '#f59e0b' },
  { name: 'Coral Blaze', hex: '#ff6b6b' },
  { name: 'Lime Burst', hex: '#84cc16' },
  { name: 'Orange Zest', hex: '#ea580c' },
  { name: 'Ruby Red', hex: '#e11d48' },
  { name: 'Rose Gold', hex: '#f43f5e' },
  { name: 'Tangerine', hex: '#f97316' },
  { name: 'Sunset Orange', hex: '#fb923c' },
  { name: 'Cinnamon', hex: '#d97706' },
  { name: 'Marigold', hex: '#eab308' },
  { name: 'Ocean Cyan', hex: '#06b6d4' },
  { name: 'Deep Teal', hex: '#14b8a6' },
  { name: 'Emerald Green', hex: '#10b981' },
  { name: 'Forest Green', hex: '#059669' },
  { name: 'Sapphire Blue', hex: '#2563eb' },
  { name: 'Royal Blue', hex: '#3b82f6' },
  { name: 'Sky Blue', hex: '#0ea5e9' },
  { name: 'Arctic Blue', hex: '#38bdf8' },
  { name: 'Mint Green', hex: '#34d399' },
  { name: 'Aqua Marine', hex: '#2dd4bf' },
  { name: 'Platinum', hex: '#9ca3af' },
  { name: 'Gold Trim', hex: '#d4a574' },
  { name: 'Silver Shadow', hex: '#94a3b8' },
  { name: 'Indigo Night', hex: '#4f46e5' },
  { name: 'Cyber Yellow', hex: '#facc15' },
  { name: 'Electric Lime', hex: '#a3e635' },
  { name: 'Neon Cyan', hex: '#22d3ee' },
  { name: 'Laser Red', hex: '#ef4444' },
  { name: 'Matrix Green', hex: '#4ade80' },
  { name: 'Digital Blue', hex: '#60a5fa' },
  { name: 'Terracotta', hex: '#c2410c' },
  { name: 'Olive Green', hex: '#65a30d' },
  { name: 'Sage', hex: '#84cc16' },
  { name: 'Sandstone', hex: '#d97706' },
  { name: 'Clay', hex: '#b45309' },
  { name: 'Moss Green', hex: '#4d7c0f' },
  { name: 'Brick Red', hex: '#dc2626' },
  { name: 'Chestnut', hex: '#92400e' },
  { name: 'Hazel', hex: '#a16207' },
  { name: 'Mahogany', hex: '#991b1b' }  
];

const getInitials = (name) => {
  if (!name) return 'OU';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getIcon = (iconName) => {
  if (!iconName) return Icons.faBars;
  if (typeof iconName === 'object' && iconName !== null && iconName.icon) {
    return iconName;
  }
  if (typeof iconName === 'string') {
    const cleanName = iconName.replace(/^fa/, '');
    const iconKey = Object.keys(Icons).find(
      key => key.toLowerCase() === cleanName.toLowerCase() || 
             key.toLowerCase() === `fa${cleanName}`.toLowerCase()
    );
    return iconKey ? Icons[iconKey] : Icons.faLayerGroup;
  }
  return iconName || Icons.faBars;
};

const shortcutMatchesKeyboardEvent = (shortcut, event) => {
  const tokens = String(shortcut || '').toLowerCase().split('+').map((token) => token.trim());
  const keyToken = tokens[tokens.length - 1];
  const expectedCode = /^[a-z]$/.test(keyToken)
    ? `Key${keyToken.toUpperCase()}`
    : /^\d$/.test(keyToken)
      ? `Digit${keyToken}`
      : null;

  return event.altKey === tokens.includes('alt')
    && event.shiftKey === tokens.includes('shift')
    && event.ctrlKey === tokens.includes('ctrl')
    && event.metaKey === (tokens.includes('cmd') || tokens.includes('meta'))
    && (expectedCode ? event.code === expectedCode : event.key.toLowerCase() === keyToken);
};

const getModuleRoleFromSubscription = (user) => {
  const subscriptions = [
    ...(user?.subscription?.summary?.subscriptions?.active || []),
    ...(user?.subscription?.summary?.subscriptions?.grace_period || []),
    ...(user?.subscription?.subscriptions?.active || []),
    ...(user?.subscription?.subscriptions?.grace_period || []),
  ];
  const selected = subscriptions.find((row) => Number(row.application_id) === Number(user?.application_id))
    || subscriptions[0];
  if (!selected) return user?.role;
  const text = `${selected.app_code || ''} ${selected.app_name || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
  const aliases = [
    ['pharmacytax', ['pharmacytax', 'taxpharmacy']],
    ['mechanicsth', ['mechanicsth', 'mechanicthermal']],
    ['mechanicsap', ['mechanicsap', 'mechaniclarge']],
    ['hardwareth', ['hardwareth', 'hardwarethermal']],
    ['hardwareap', ['hardwareap', 'hardwarelarge']],
    ['autosth', ['autosth', 'autothermal']],
    ['autosap', ['autosap', 'autolarge']],
    ['cosmetics', ['cosmetics', 'cosmetic', 'ozcmis']],
    ['pharmacy', ['pharmacy', 'medical']],
    ['electronics', ['electronics', 'electronic']],
    ['appliances', ['appliances', 'appliance']],
    ['fabrics', ['fabrics', 'fabric', 'tailor']],
    ['solar', ['solar']], ['pvc', ['pvc']], ['salanty', ['salanty']],
    ['pos', ['pos']], ['po', ['purchaseorder']],
  ];
  const moduleKey = aliases.find(([, terms]) => terms.some((term) => text.includes(term)))?.[0];
  if (!moduleKey) return user?.role;
  const prefix = /^(admin|manager|viewer|salesman|user)_/.exec(user?.role || '')?.[1] || 'user';
  return `${prefix}_${moduleKey}`;
};

const LOGIN_PATH = '/';
const LOGOUT_TIMEOUT_MS = 5000;

const CURRENT_DEVICE_AUTH_KEYS = [
  'user',
  'user_role',
  'auth_timestamp',
  'current_shop',
  'session_id',
  'authToken',
  'access_token',
  'refresh_token'
];

const settleWithin = (promise, timeoutMs = LOGOUT_TIMEOUT_MS) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(
      () => reject(new Error('Logout request timed out.')),
      timeoutMs
    );
  });

  return Promise.race([Promise.resolve(promise), timeout])
    .finally(() => window.clearTimeout(timeoutId));
};

const clearCurrentDeviceAuthState = () => {
  clearSecureSession(true);
  CURRENT_DEVICE_AUTH_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const PREFERENCE_KEYS = [
  'auth_theme_id', 'auth_dark_mode', 'auth_language', 'auth_remember_me', 'auth_saved_username',
  'ourzest_theme', 'ourzest_theme_id', 'ourzest_darkMode', 'ourzest_language',
  'ourzest_remember', 'ourzest_username', 'rememberMe', 'savedUsername'
];

const snapshotPreferences = () => PREFERENCE_KEYS.reduce((snapshot, key) => {
  const value = localStorage.getItem(key);
  if (value !== null) snapshot[key] = value;
  return snapshot;
}, {});

const restorePreferences = (snapshot) => {
  Object.entries(snapshot).forEach(([key, value]) => localStorage.setItem(key, value));
};

// ==============================================
// MENU CACHE CONSTANTS FOR PERSISTENCE
// ==============================================
const MENU_CACHE_KEY = 'ourzest_menu_cache';
const MENU_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// ==============================================
// MAIN DASHBOARD COMPONENT
// ==============================================
const DashboardBusinessManager = () => {
  const auth = useAuth();
  const { user, logout, setUser, currentShop } = auth;
  const navigate = useNavigate();

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const logoutInFlightRef = useRef(false);
  
  // Use refs for stable values that don't need re-renders
  const menuLoadInFlightRef = useRef(false);
  const languageRequestRef = useRef(0);
  const mountedRef = useRef(true);
  const silentRefreshTimerRef = useRef(null);
  
  // Track active states with refs
  const activeMenuRef = useRef('dashboard');
  const activeSubmenuRef = useRef('');

  // ===== STATE DECLARATIONS =====
  const moduleConfig = getModuleByRole(getModuleRoleFromSubscription(user)) || getModuleByRole(user?.role);
  const supportedLanguageCodes = ['en', 'ur'];
  
  const [businessData, setBusinessData] = useState({
    businessname: user?.businessname || user?.shopname || 'OurZest Enterprise',
    bussinesslogo: null,
    profile_image: null,
    fullname: user?.fullname || user?.username || 'User'
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileFocusedMenu, setMobileFocusedMenu] = useState(null);

  const [activeMenu, setActiveMenu] = useState(() => {
    const saved = localStorage.getItem('ourzest_active_menu') || 'dashboard';
    activeMenuRef.current = saved;
    return saved;
  });
  const [activeSubmenu, setActiveSubmenu] = useState(() => {
    const saved = localStorage.getItem('ourzest_active_submenu') || '';
    activeSubmenuRef.current = saved;
    return saved;
  });
  const [expandedMenuIds, setExpandedMenuIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('ourzest_expanded_menus') || '[]');
    } catch {
      return [];
    }
  });

  // Update refs when state changes
  useEffect(() => {
    activeMenuRef.current = activeMenu;
    localStorage.setItem('ourzest_active_menu', activeMenu);
  }, [activeMenu]);

  useEffect(() => {
    activeSubmenuRef.current = activeSubmenu;
    localStorage.setItem('ourzest_active_submenu', activeSubmenu);
  }, [activeSubmenu]);

  useEffect(() => {
    localStorage.setItem('ourzest_expanded_menus', JSON.stringify(expandedMenuIds));
  }, [expandedMenuIds]);

  const [collapsedSectionIds, setCollapsedSectionIds] = useState([]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const navigationOrderStorageKey = `ourzest_navigation_order_${moduleConfig?.moduleId || 'default'}`;
  const [navigationOrder, setNavigationOrder] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(navigationOrderStorageKey) || '{}');
    } catch {
      return {};
    }
  });

  const [desktopSearchQuery, setDesktopSearchQuery] = useState('');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [desktopSearchExpanded, setDesktopSearchExpanded] = useState(false);

  const [formContent, setFormContent] = useState(null);
  const [breadcrumbItems, setBreadcrumbItems] = useState([
    { id: 'dashboard', label: 'Dashboard', isClickable: true }
  ]);
  const [, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const requested = localStorage.getItem('ourzest_language')
      || auth?.currentLanguage || auth?.language || user?.language || user?.preferred_language || 'en';
    return requested === 'ur' ? 'ur' : 'en';
  });
  const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
  const [translations, setTranslations] = useState({});
  const [translatedMenus, setTranslatedMenus] = useState([]);
  const [menuAccessLoading, setMenuAccessLoading] = useState(true);
  const [menuAccessError, setMenuAccessError] = useState('');
  const [resolvedApplicationId, setResolvedApplicationId] = useState(() => Number(
    user?.application_id
    || user?.subscription?.summary?.subscriptions?.active?.[0]?.application_id
    || user?.subscription?.subscriptions?.active?.[0]?.application_id
    || 0
  ) || null);

  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 12, maxHeight: 0 });
  const hoverTimerRef = useRef(null);

  const [showShopSwitcher, setShowShopSwitcher] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  
  const [userShops, setUserShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);

  const shopSwitcherRef = useRef(null);
  const themePickerRef = useRef(null);
  const languagePickerRef = useRef(null);
  const desktopSearchRef = useRef(null);
  const desktopSearchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const shortcutPanelRef = useRef(null);

  // ===== MENU CACHE FUNCTIONS =====
  const getCachedMenus = useCallback(() => {
    try {
      const cached = localStorage.getItem(MENU_CACHE_KEY);
      if (!cached) return null;
      
      const parsed = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid (within 5 minutes)
      if (parsed.timestamp && (now - parsed.timestamp) < MENU_CACHE_DURATION) {
        return parsed.menus;
      }
      
      // Clear expired cache
      localStorage.removeItem(MENU_CACHE_KEY);
      return null;
    } catch (error) {
      console.warn('Error reading menu cache:', error);
      return null;
    }
  }, []);

  const cacheMenus = useCallback((menus) => {
    try {
      const cacheData = {
        timestamp: Date.now(),
        menus: menus
      };
      localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error caching menus:', error);
    }
  }, []);

  // ===== MEMOIZED CALLBACKS =====
  const handleSecureLogout = useCallback(async () => {
    if (logoutInFlightRef.current) return;

    logoutInFlightRef.current = true;
    const preferences = snapshotPreferences();
    setIsLoggingOut(true);
    setMobileSheetOpen(false);
    setShowShortcuts(false);
    setShowThemePicker(false);
    setShowShopSwitcher(false);
    setShowLanguagePicker(false);

    try {
      await settleWithin(rootAction('root_session_api.php', 'logout', {
        scope: 'current_session',
        all_devices: false
      }));
    } catch (error) {
      console.warn('Server logout failed; ending this device session locally.', error);
    }

    clearCurrentDeviceAuthState();

    try {
      if (typeof logout === 'function') {
        await settleWithin(logout());
      }
    } catch (error) {
      console.warn('AuthContext logout cleanup failed; local session was already removed.', error);
    }
    restorePreferences(preferences);

    window.dispatchEvent(new CustomEvent('ourzest:logged-out', {
      detail: { scope: 'current-device' }
    }));
    navigate(LOGIN_PATH, { replace: true });
  }, [logout, navigate]);

  const applyTheme = useCallback((colorHex) => {
    const hexColor = /^#[0-9A-F]{6}$/i.test(colorHex) ? colorHex : '#6366f1';
    localStorage.setItem('ourzest_theme', hexColor);
    setCurrentTheme(hexColor);

    let hex = hexColor.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.substring(0, 2), 16) || 99;
    const g = parseInt(hex.substring(2, 4), 16) || 102;
    const b = parseInt(hex.substring(4, 6), 16) || 241;

    document.documentElement.style.setProperty('--primary-color', hexColor);
    document.documentElement.style.setProperty('--primary-rgb', `${r}, ${g}, ${b}`);
    document.documentElement.style.setProperty('--primary-shadow', `rgba(${r}, ${g}, ${b}, 0.2)`);
  }, []);

  // ===== MEMOIZED DERIVED DATA =====
  const stockMenuItem = useMemo(() => {
    return translatedMenus.find(m => 
      m.id.toLowerCase().includes('stock') || 
      m.id.toLowerCase().includes('inventory') ||
      m.label.toLowerCase().includes('stock')
    );
  }, [translatedMenus]);

  const saleMenuItem = useMemo(() => {
    return translatedMenus.find(m => 
      m.id.toLowerCase().includes('sale') || 
      m.id.toLowerCase().includes('pos') ||
      m.label.toLowerCase().includes('sale')
    );
  }, [translatedMenus]);

  const purchaseMenuItem = useMemo(() => {
    return translatedMenus.find(m => 
      m.id.toLowerCase().includes('purchase') || 
      m.id.toLowerCase().includes('po') ||
      m.label.toLowerCase().includes('purchase')
    );
  }, [translatedMenus]);

  const mobileQuickMenus = useMemo(() => {
    const preferred = [stockMenuItem, saleMenuItem, purchaseMenuItem].filter(Boolean);
    const seen = new Set(preferred.map((menu) => menu.id));
    const fallback = translatedMenus
      .filter((menu) => !seen.has(menu.id))
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    return [...preferred, ...fallback].slice(0, 3);
  }, [translatedMenus, stockMenuItem, saleMenuItem, purchaseMenuItem]);

  // ===== EFFECTS WITH PROPER CLEANUP =====
  
  // Mount/Unmount cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
      if (silentRefreshTimerRef.current) {
        clearTimeout(silentRefreshTimerRef.current);
      }
    };
  }, []);

  // Language direction update
  useEffect(() => {
    const language = LANGUAGES[currentLanguage] || LANGUAGES.en;
    const isRtl = language.dir === 'rtl';
    
    requestAnimationFrame(() => {
      document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
      document.documentElement.lang = currentLanguage;
      document.body.style.direction = isRtl ? 'rtl' : 'ltr';
      document.documentElement.style.setProperty('--direction', isRtl ? 'rtl' : 'ltr');
      document.documentElement.classList.toggle('ourzest-rtl', isRtl);
      document.body.classList.toggle('ourzest-rtl', isRtl);
      localStorage.setItem('ourzest_language', currentLanguage);
    });
  }, [currentLanguage]);

  // Viewport meta tag
  useEffect(() => {
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'viewport';
      document.head.appendChild(meta);
    }
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';

    if (window.screen && window.screen.orientation && window.screen.orientation.lock) {
      window.screen.orientation.lock('portrait').catch(() => {});
    }

    const handleTouchMove = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // ===== FETCH FUNCTIONS =====
  const fetchBusinessInfo = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_business_info',
          username: user?.username,
          user_id: user?.id
        })
      });
      const data = await response.json();
      if (mountedRef.current && data && (data.status === 'success' || data.businessname)) {
        setBusinessData({
          businessname: data.businessname || user?.businessname || 'OurZest App',
          bussinesslogo: data.bussinesslogo || null,
          profile_image: data.profile_image || null,
          fullname: data.fullname || user?.fullname || user?.username || 'User'
        });

        const storedTheme = localStorage.getItem('ourzest_theme') || data.themecolor || '#6366f1';
        applyTheme(storedTheme);
      }
    } catch (err) {
      console.warn("Error fetching business info:", err);
      const storedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
      applyTheme(storedTheme);
    }
  }, [user, applyTheme]);

  // ===== RENDER HELPERS =====
  const renderAuthorizedComponent = useCallback((item) => {
    if (!item?.component) return null;
    return React.createElement(item.component, {
      ...(item.componentProps || {}),
      accessLevel: item.accessLevel,
      menuAccess: item,
      permissions: {
        view: item.canView,
        create: item.canCreate,
        edit: item.canEdit,
        delete: item.canDelete,
        manage: item.canManage,
      },
    });
  }, []);

  const renderUnmappedMenu = useCallback((item) => (
    <div style={{ padding: 24 }}>
      <div style={{ padding: 24, borderRadius: 18, background: 'var(--card-bg, #fff)', boxShadow: '0 12px 40px rgba(15,23,42,.08)' }}>
        <h2 style={{ marginTop: 0 }}>{item?.translatedName || item?.label || 'Authorized menu'}</h2>
        <p>This menu is authorized through {item?.accessSource || 'your package'}, but it has no usable React component or registered route.</p>
        <p style={{ marginBottom: 4 }}>Database menu code: <strong>{item?.menuCode || item?.id || 'not set'}</strong></p>
        <p style={{ marginTop: 0 }}>React component key: <strong>{item?.componentKey || 'not set'}</strong></p>
      </div>
    </div>
  ), []);

  // ===== NAVIGATION HANDLERS =====
  const updateBreadcrumbs = useCallback((menuId, submenuId = null) => {
    const crumbs = [
      { id: 'dashboard', label: translations.dashboard || 'Dashboard', isClickable: true }
    ];

    if (menuId && menuId !== 'dashboard') {
      const menu = translatedMenus.find(m => m.id === menuId);
      if (menu) {
        crumbs.push({
          id: menu.id,
          label: menu.translatedName || menu.label,
          isClickable: true
        });
      }
    }

    if (submenuId) {
      const menu = translatedMenus.find(m => m.id === menuId);
      const sub = menu?.submenus?.find(s => s.id === submenuId);
      if (sub) {
        crumbs.push({
          id: sub.id,
          label: sub.translatedName || sub.label,
          isClickable: false
        });
      }
    }

    setBreadcrumbItems(crumbs);
  }, [translations, translatedMenus]);

  // Main navigation handler
  const handleMenuClick = useCallback((menuId) => {
    // Don't navigate if it's the same menu
    if (menuId === activeMenuRef.current && !activeSubmenuRef.current) {
      return;
    }

    const menu = translatedMenus.find(m => m.id === menuId);
    if (menuId !== 'dashboard' && !menu) {
      return;
    }
    
    const hasSubmenus = menu?.submenus && menu.submenus.length > 0;

    // Update expansion state
    if (hasSubmenus) {
      setExpandedMenuIds(prev => prev[0] === menuId ? [] : [menuId]);
    } else {
      setExpandedMenuIds([]);
    }

    // Update active states
    setActiveMenu(menuId);
    setActiveSubmenu('');
    
    // Close all popups
    setHoveredMenu(null);
    setMobileSheetOpen(false);
    setMobileFocusedMenu(null);
    setShowShortcuts(false);
    setShowThemePicker(false);
    setShowShopSwitcher(false);
    setShowLanguagePicker(false);

    // Load content
    if (menuId === 'dashboard') {
      const DashboardComp = moduleConfig?.dashboardComponent || (() => <div className="p-4">{translations.dashboard || 'Dashboard'}</div>);
      setFormContent(<DashboardComp onNavigate={handleMenuNavigation} updateBreadcrumbs={updateBreadcrumbs} />);
      updateBreadcrumbs('dashboard');
    } else if (hasSubmenus) {
      // Parent menu with submenus - just expand, don't load content
      updateBreadcrumbs(menuId);
    } else if (menu?.canView && menu?.component) {
      setFormContent(renderAuthorizedComponent(menu));
      updateBreadcrumbs(menuId);
    } else if (menu?.canView && menu?.route) {
      if (/^https?:\/\//i.test(menu.route)) {
        window.open(menu.route, '_blank', 'noopener,noreferrer');
      } else {
        const normalized = `/${String(menu.route).replace(/^\/+/, '')}`.replace(/\/+$/, '') || '/';
        navigate(normalized);
      }
      updateBreadcrumbs(menuId);
    } else if (menu?.canView) {
      setFormContent(renderUnmappedMenu(menu));
      updateBreadcrumbs(menuId);
    }
  }, [translatedMenus, moduleConfig, translations, navigate, renderAuthorizedComponent, renderUnmappedMenu, updateBreadcrumbs]);

  const handleSubmenuClick = useCallback((menuId, submenuId) => {
    // Don't navigate if it's the same submenu
    if (menuId === activeMenuRef.current && submenuId === activeSubmenuRef.current) {
      return;
    }

    const menu = translatedMenus.find(m => m.id === menuId);
    const sub = menu?.submenus?.find(s => s.id === submenuId);
    if (!sub?.canView) {
      return;
    }

    // Update active states
    setActiveMenu(menuId);
    setActiveSubmenu(submenuId);
    
    // Close all popups
    setHoveredMenu(null);
    setMobileSheetOpen(false);
    setMobileFocusedMenu(null);
    setShowShortcuts(false);
    setShowThemePicker(false);
    setShowShopSwitcher(false);
    setShowLanguagePicker(false);

    // Ensure parent menu is expanded
    setExpandedMenuIds([menuId]);

    // Load content
    if (sub.component) {
      setFormContent(renderAuthorizedComponent(sub));
    } else if (sub.route) {
      if (/^https?:\/\//i.test(sub.route)) {
        window.open(sub.route, '_blank', 'noopener,noreferrer');
      } else {
        const normalized = `/${String(sub.route).replace(/^\/+/, '')}`.replace(/\/+$/, '') || '/';
        navigate(normalized);
      }
    } else {
      setFormContent(renderUnmappedMenu(sub));
    }
    updateBreadcrumbs(menuId, submenuId);
  }, [translatedMenus, navigate, renderAuthorizedComponent, renderUnmappedMenu, updateBreadcrumbs]);

  const handleMenuNavigation = useCallback((menuId, submenuId = null) => {
    if (submenuId) {
      handleSubmenuClick(menuId, submenuId);
    } else {
      handleMenuClick(menuId);
    }
  }, [handleSubmenuClick, handleMenuClick]);

  // ===== LOAD TRANSLATIONS AND MENUS =====
  const loadTranslationsAndMenus = useCallback(async () => {
    if (!user?.id || menuLoadInFlightRef.current || !mountedRef.current) return;
    
    // Check cache first
    const cachedMenus = getCachedMenus();
    if (cachedMenus && cachedMenus.length > 0 && !isInitialized) {
      if (mountedRef.current) {
        setTranslatedMenus(cachedMenus);
        setMenuAccessLoading(false);
        
        // Restore state from cache
        const savedMenu = localStorage.getItem('ourzest_active_menu') || 'dashboard';
        const savedSubmenu = localStorage.getItem('ourzest_active_submenu') || '';
        
        if (savedMenu === 'dashboard') {
          const DashboardComp = moduleConfig?.dashboardComponent || (() => <div className="p-4">{translations.dashboard || 'Dashboard'}</div>);
          setFormContent(<DashboardComp onNavigate={handleMenuNavigation} updateBreadcrumbs={updateBreadcrumbs} />);
          updateBreadcrumbs('dashboard');
        } else {
          const currentMenu = cachedMenus.find(m => m.id === savedMenu);
          if (currentMenu) {
            if (savedSubmenu) {
              const sub = currentMenu.submenus.find(s => s.id === savedSubmenu);
              if (sub?.canView && sub.component) {
                setFormContent(renderAuthorizedComponent(sub));
                updateBreadcrumbs(savedMenu, savedSubmenu);
                setActiveSubmenu(savedSubmenu);
              } else {
                handleMenuClick(savedMenu);
              }
            } else {
              handleMenuClick(savedMenu);
            }
          } else {
            const DashboardComp = moduleConfig?.dashboardComponent || (() => <div className="p-4">{translations.dashboard || 'Dashboard'}</div>);
            setFormContent(<DashboardComp onNavigate={handleMenuNavigation} updateBreadcrumbs={updateBreadcrumbs} />);
            updateBreadcrumbs('dashboard');
            setActiveMenu('dashboard');
            setActiveSubmenu('');
          }
        }
        setIsInitialized(true);
      }
      
      // Silent refresh in background
      if (silentRefreshTimerRef.current) {
        clearTimeout(silentRefreshTimerRef.current);
      }
      silentRefreshTimerRef.current = setTimeout(() => {
        performMenuLoad();
      }, 1000);
      
      return;
    }
    
    await performMenuLoad();
    
    async function performMenuLoad() {
      menuLoadInFlightRef.current = true;
      setMenuAccessLoading(true);
      setMenuAccessError('');

      try {
        const trans = await translator.getTranslations(currentLanguage);
        if (!mountedRef.current) return;
        setTranslations(trans);

        let applicationId = Number(user?.application_id || 0);
        if (!applicationId) {
          const subscriptionRows = [
            ...(user?.subscription?.summary?.subscriptions?.active || []),
            ...(user?.subscription?.summary?.subscriptions?.grace_period || []),
            ...(user?.subscription?.subscriptions?.active || []),
            ...(user?.subscription?.subscriptions?.grace_period || []),
          ];
          applicationId = Number(subscriptionRows.find((row) => row?.application_id)?.application_id || 0);
        }
        if (!applicationId && user?.application_code) {
          const apps = await rootApi('root_control_center.php?action=applications');
          const matched = (apps.applications || []).find((app) =>
            String(app.app_code).toLowerCase() === String(user.application_code).toLowerCase()
          );
          applicationId = Number(matched?.id || 0);
        }
        if (!applicationId) {
          throw new Error('No application is attached to this login session.');
        }
        
        if (mountedRef.current) {
          setResolvedApplicationId(applicationId);
          if (!user?.application_id) {
            const repairedUser = { ...user, application_id: applicationId };
            setUser(repairedUser);
            localStorage.setItem('user', JSON.stringify(repairedUser));
          }
        }

        const shopId = Number(currentShop?.shop_id || user?.shop_id || 0) || null;
        const query = new URLSearchParams({
          action: 'effective_access',
          application_id: String(applicationId),
        });
        if (shopId) query.set('shop_id', String(shopId));

        const [accessResult, menuResult] = await Promise.all([
          rootApi(`root_control_center.php?${query}`),
          rootApi(`root_control_center.php?action=menus&application_id=${applicationId}`),
        ]);

        if (!mountedRef.current) return;

        const catalogueRows = menuResult.menus || [];
        const catalogueById = new Map(catalogueRows.map((row) => [Number(row.id), row]));
        const deniedIds = new Set((accessResult.denied_menu_ids || []).map(Number));
        const hasDeniedAncestor = (row) => {
          let id = Number(row?.menu_id || row?.id || 0);
          const visited = new Set();
          while (id && !visited.has(id)) {
            if (deniedIds.has(id)) return true;
            visited.add(id);
            id = Number(catalogueById.get(id)?.parent_menu_id || 0);
          }
          return false;
        };
        const accessRows = (accessResult.access || []).filter((row) => !hasDeniedAncestor(row));

        const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const menuKeyVariants = (value) => {
          const raw = String(value || '').trim().toLowerCase();
          if (!raw) return [];
          const values = new Set([normalize(raw)]);
          const withoutPrefix = raw
            .replace(/^[a-z][_-]+/, '')
            .replace(/^(menu|submenu|sub|admin|user)[_-]+/, '');
          values.add(normalize(withoutPrefix));
          [...values].forEach((key) => {
            values.add(key.replace(/s(list|form|report|history|detail|details)$/i, '$1'));
          });
          return [...values].filter(Boolean);
        };
        const staticEntries = [];
        (moduleConfig?.menus || []).forEach((menu, menuIndex) => {
          staticEntries.push({ ...menu, position: menu.position ?? menuIndex, staticParent: null });
          (menu.submenus || []).forEach((sub, submenuIndex) => staticEntries.push({
            ...sub,
            position: sub.position ?? submenuIndex,
            staticParent: menu,
            groupId: sub.groupId || menu.groupId,
            group: sub.group || menu.group,
            section: sub.section || menu.section,
            groupName: sub.groupName || menu.groupName,
            sectionName: sub.sectionName || menu.sectionName,
            groupIcon: sub.groupIcon || menu.groupIcon,
          }));
        });

        const resolveStatic = (row) => {
          const candidates = [row.menu_code, row.route, row.menu_name].flatMap(menuKeyVariants);
          const matches = staticEntries.filter((entry) => {
            const keys = [
              entry.id, entry.menu_code, entry.code, entry.route, entry.label,
              ...(entry.aliases || []), ...(entry.menuCodes || []),
            ].flatMap(menuKeyVariants);
            return candidates.some((candidate) => keys.includes(candidate));
          });
          if (matches.length <= 1) return matches[0] || null;

          const databaseExpectsChild = Number(row.parent_menu_id || 0) > 0;
          return matches.find((entry) => databaseExpectsChild
            ? Boolean(entry.staticParent)
            : !entry.staticParent) || matches[0] || null;
        };

        const accessById = new Map(accessRows.map((row) => [Number(row.menu_id), row]));
        const visibleIds = new Set(accessById.keys());

        accessRows.forEach((row) => {
          let parentId = Number(row.parent_menu_id || 0);
          const visited = new Set();
          while (parentId && !visited.has(parentId)) {
            if (deniedIds.has(parentId)) break;
            visited.add(parentId);
            visibleIds.add(parentId);
            parentId = Number(catalogueById.get(parentId)?.parent_menu_id || 0);
          }
        });

        const makeItem = (row, structuralOnly = false) => {
          const grant = accessById.get(Number(row.id || row.menu_id));
          const specialItem = resolveSpecialMenu(row, {
            applicationId,
            applicationCode: user?.application_code || moduleConfig?.moduleId,
            role: user?.role,
            shopId,
          });
          const staticItem = resolveStatic(row)
            || (specialItem?.canonicalId
              ? staticEntries.find((entry) => menuKeyVariants(entry.id).includes(normalize(specialItem.canonicalId)))
              : null);
          const menuCode = specialItem?.canonicalId || staticItem?.id || row.menu_code || `menu_${row.id}`;
          const accessLevel = grant?.access_level || null;
          let databaseComponentProps = {};
          try {
            const parsed = typeof row.component_props_json === 'string'
              ? JSON.parse(row.component_props_json)
              : row.component_props_json;
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) databaseComponentProps = parsed;
          } catch (error) {
            console.warn(`Invalid component props for menu ${row.menu_code}:`, error);
          }
          return {
            ...(staticItem || {}),
            id: String(menuCode),
            menuCode: row.menu_code || null,
            databaseId: Number(row.id || row.menu_id),
            label: row.menu_name || staticItem?.label || menuCode,
            translatedName: MENU_TRANSLATIONS[currentLanguage]?.[menuCode]
              || moduleConfig?.menuTranslations?.[currentLanguage]?.[menuCode]
              || trans[menuCode] || row.menu_name || staticItem?.label || menuCode,
            icon: staticItem?.icon || row.icon || 'faLayerGroup',
            route: row.route || staticItem?.route || null,
            componentKey: row.component_key || specialItem?.componentKey || staticItem?.componentKey || null,
            component: structuralOnly ? null : (specialItem?.component || staticItem?.component || null),
            componentProps: {
              ...(staticItem?.componentProps || {}),
              ...(specialItem?.componentProps || {}),
              ...databaseComponentProps,
            },
            accessLevel,
            accessSource: grant?.access_source || null,
            canView: Boolean(grant),
            canCreate: ['create', 'edit', 'delete', 'manage'].includes(accessLevel),
            canEdit: ['edit', 'delete', 'manage'].includes(accessLevel),
            canDelete: ['delete', 'manage'].includes(accessLevel),
            canManage: accessLevel === 'manage',
            displayOrder: Number(row.display_order ?? staticItem?.displayOrder ?? staticItem?.position ?? 0),
            position: Number(row.display_order ?? row.position ?? staticItem?.position ?? 0),
            configuredParentId: staticItem?.staticParent?.id || null,
            isConfiguredRoot: Boolean(staticItem && !staticItem.staticParent),
            submenus: [],
          };
        };

        const visibleRows = catalogueRows
          .filter((row) => visibleIds.has(Number(row.id)) && !hasDeniedAncestor(row))
          .sort((a, b) => Number(a.display_order || 0) - Number(b.display_order || 0)
            || String(a.menu_name).localeCompare(String(b.menu_name)));
        const itemByDatabaseId = new Map(visibleRows.map((row) => [
          Number(row.id),
          makeItem(row, !accessById.has(Number(row.id))),
        ]));
        const itemByCanonicalId = new Map(
          Array.from(itemByDatabaseId.values()).map((item) => [item.id, item])
        );
        const roots = [];

        visibleRows.forEach((row) => {
          const item = itemByDatabaseId.get(Number(row.id));
          const configuredParent = item.configuredParentId
            ? itemByCanonicalId.get(item.configuredParentId)
            : null;
          const databaseParent = itemByDatabaseId.get(Number(row.parent_menu_id || 0));
          const parent = configuredParent || (item.isConfiguredRoot ? null : databaseParent);
          if (parent) {
            parent.submenus.push(item);
            parent.submenus.sort((a, b) => (a.position || 0) - (b.position || 0));
          } else roots.push(item);
        });

        const flattenChildren = (items, depth = 1) => items.flatMap((item) => [
          { ...item, depth, submenus: [] },
          ...flattenChildren(item.submenus || [], depth + 1),
        ]);
        
        roots.sort((a, b) => (a.position || 0) - (b.position || 0));
        
        const authorizedMenus = roots.map((root) => ({
          ...root,
          submenus: flattenChildren(root.submenus || []),
        }));

        if (mountedRef.current) {
          setTranslatedMenus(authorizedMenus);
          // Cache the menus for offline persistence
          cacheMenus(authorizedMenus);
          
          // Only restore state if we have menus loaded and it's initial load
          if (!isInitialized && authorizedMenus.length > 0) {
            const savedMenu = localStorage.getItem('ourzest_active_menu') || 'dashboard';
            const savedSubmenu = localStorage.getItem('ourzest_active_submenu') || '';
            
            if (savedMenu === 'dashboard') {
              const DashboardComp = moduleConfig?.dashboardComponent || (() => <div className="p-4">{trans.dashboard || 'Dashboard'}</div>);
              setFormContent(<DashboardComp onNavigate={handleMenuNavigation} updateBreadcrumbs={updateBreadcrumbs} />);
              updateBreadcrumbs('dashboard');
            } else {
              const currentMenu = authorizedMenus.find(m => m.id === savedMenu);
              if (currentMenu) {
                if (savedSubmenu) {
                  const sub = currentMenu.submenus.find(s => s.id === savedSubmenu);
                  if (sub?.canView && sub.component) {
                    setFormContent(renderAuthorizedComponent(sub));
                    updateBreadcrumbs(savedMenu, savedSubmenu);
                    setActiveSubmenu(savedSubmenu);
                  } else {
                    handleMenuClick(savedMenu);
                  }
                } else {
                  handleMenuClick(savedMenu);
                }
              } else {
                // Menu not found, go to dashboard
                const DashboardComp = moduleConfig?.dashboardComponent || (() => <div className="p-4">{trans.dashboard || 'Dashboard'}</div>);
                setFormContent(<DashboardComp onNavigate={handleMenuNavigation} updateBreadcrumbs={updateBreadcrumbs} />);
                updateBreadcrumbs('dashboard');
                setActiveMenu('dashboard');
                setActiveSubmenu('');
              }
            }
            setIsInitialized(true);
          } else if (isInitialized) {
            // Just update the menu list, don't change current view
            // But verify current active menu still exists
            const currentMenuExists = activeMenuRef.current === 'dashboard' || 
              authorizedMenus.some(m => m.id === activeMenuRef.current);
            
            if (!currentMenuExists && activeMenuRef.current !== 'dashboard') {
              // Current menu no longer exists, go to dashboard
              const DashboardComp = moduleConfig?.dashboardComponent || (() => <div className="p-4">{trans.dashboard || 'Dashboard'}</div>);
              setFormContent(<DashboardComp onNavigate={handleMenuNavigation} updateBreadcrumbs={updateBreadcrumbs} />);
              updateBreadcrumbs('dashboard');
              setActiveMenu('dashboard');
              setActiveSubmenu('');
            }
          }
        }
      } catch (error) {
        console.error('Effective menu access load failed:', error);
        if (mountedRef.current) {
          // On error, try to use cached menus
          const fallbackMenus = getCachedMenus();
          if (fallbackMenus && fallbackMenus.length > 0) {
            setTranslatedMenus(fallbackMenus);
            setMenuAccessError('');
          } else {
            setTranslatedMenus([]);
            setMenuAccessError(error.message || 'Could not load menu access.');
          }
        }
      } finally {
        if (mountedRef.current) {
          menuLoadInFlightRef.current = false;
          setMenuAccessLoading(false);
        }
      }
    }
  }, [user, currentLanguage, currentShop, moduleConfig, setUser, handleMenuClick, handleMenuNavigation, renderAuthorizedComponent, updateBreadcrumbs, isInitialized, getCachedMenus, cacheMenus]);

  // ===== ORDER NAVIGATION =====
  const orderNavigationItems = useCallback((items, scopeKey) => {
    const savedIds = navigationOrder[scopeKey] || [];
    const savedIndex = new Map(savedIds.map((id, index) => [id, index]));
    const originalIndex = new Map(items.map((item, index) => [item.id, index]));
    return [...items].sort((a, b) => {
      const aSaved = savedIndex.has(a.id);
      const bSaved = savedIndex.has(b.id);
      if (aSaved && bSaved) return savedIndex.get(a.id) - savedIndex.get(b.id);
      if (aSaved) return -1;
      if (bSaved) return 1;
      const aPosition = Number.isFinite(a.position) ? a.position : a.displayOrder;
      const bPosition = Number.isFinite(b.position) ? b.position : b.displayOrder;
      if (Number.isFinite(aPosition) && Number.isFinite(bPosition) && aPosition !== bPosition) {
        return aPosition - bPosition;
      }
      return originalIndex.get(a.id) - originalIndex.get(b.id);
    });
  }, [navigationOrder]);

  const getOrderedSubmenus = useCallback((menu) => (
    orderNavigationItems(menu?.submenus || [], `menu:${menu?.id}`)
  ), [orderNavigationItems]);

  const moveNavigationItem = useCallback((scopeKey, itemId, direction, items) => {
    const ordered = orderNavigationItems(items, scopeKey);
    const currentIndex = ordered.findIndex((item) => item.id === itemId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= ordered.length) return;

    const nextIds = ordered.map((item) => item.id);
    [nextIds[currentIndex], nextIds[nextIndex]] = [nextIds[nextIndex], nextIds[currentIndex]];
    setNavigationOrder((previous) => {
      const next = { ...previous, [scopeKey]: nextIds };
      localStorage.setItem(navigationOrderStorageKey, JSON.stringify(next));
      return next;
    });
  }, [navigationOrderStorageKey, orderNavigationItems]);

  // ===== SEARCH =====
  const getSearchResults = useCallback((query) => {
    if (!query || !query.trim()) return [];
    const q = query.toLowerCase().trim();
    const results = [];

    translatedMenus.forEach(menu => {
      const menuName = (menu.translatedName || menu.label || '').toLowerCase();
      if (menuName.includes(q)) {
        results.push({
          type: 'menu',
          menuId: menu.id,
          submenuId: null,
          title: menu.translatedName || menu.label,
          icon: menu.icon,
          parent: 'Main Menu'
        });
      }

      if (menu.submenus) {
        menu.submenus.forEach(sub => {
          const subName = (sub.translatedName || sub.label || '').toLowerCase();
          if (subName.includes(q)) {
            results.push({
              type: 'submenu',
              menuId: menu.id,
              submenuId: sub.id,
              title: sub.translatedName || sub.label,
              icon: sub.icon || menu.icon,
              parent: menu.translatedName || menu.label
            });
          }
        });
      }
    });

    return results;
  }, [translatedMenus]);

  const desktopSearchResults = useMemo(
    () => getSearchResults(desktopSearchQuery),
    [desktopSearchQuery, getSearchResults]
  );
  const mobileSearchResults = useMemo(
    () => getSearchResults(mobileSearchQuery),
    [mobileSearchQuery, getSearchResults]
  );

  // ===== GROUPED MENUS =====
  const groupedMenuSections = useMemo(() => {
    const groupsMap = new Map();
    const defaultGroups = moduleConfig?.menuGroups || moduleConfig?.menuSections || [];

    if (Array.isArray(defaultGroups)) {
      defaultGroups.forEach(g => {
        const id = g.id || g.name;
        groupsMap.set(id, {
          id: id,
          name: GROUP_TRANSLATIONS[currentLanguage]?.[id] || g.name || g.label || id,
          icon: g.icon || 'faLayerGroup',
          order: g.order || 99,
          menus: []
        });
      });
    }

    translatedMenus.forEach(menu => {
      const groupId = menu.groupId || menu.group || menu.section || 'main';
      const groupName = menu.sectionName || menu.groupName || menu.section || menu.group || (groupId !== 'main' ? groupId : 'Main Navigation');

      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, {
          id: groupId,
          name: GROUP_TRANSLATIONS[currentLanguage]?.[groupId] || groupName,
          icon: menu.groupIcon || 'faLayerGroup',
          order: 99,
          menus: []
        });
      }
      groupsMap.get(groupId).menus.push(menu);
    });

    groupsMap.forEach((group) => {
      group.menus = orderNavigationItems(group.menus, `group:${group.id}`);
    });

    return Array.from(groupsMap.values())
      .filter(group => group.menus.length > 0)
      .sort((a, b) => (a.order || 99) - (b.order || 99));
  }, [translatedMenus, moduleConfig, currentLanguage, orderNavigationItems]);

  // ===== SHORTCUTS =====
  const availableShortcutEntries = useMemo(() => {
    return (moduleConfig?.shortcuts || []).reduce((entries, shortcut) => {
      if (shortcut.action) {
        entries.push({ ...shortcut, groupName: 'General' });
        return entries;
      }

      const menu = shortcut.menuId === 'dashboard'
        ? { id: 'dashboard', label: translations.dashboard || 'Dashboard', groupId: 'management' }
        : translatedMenus.find((item) => item.id === shortcut.menuId);
      if (!menu) return entries;

      const submenu = shortcut.submenuId
        ? menu.submenus?.find((item) => item.id === shortcut.submenuId && item.canView)
        : null;
      if (shortcut.submenuId && !submenu) return entries;

      const group = (moduleConfig?.menuGroups || []).find((item) => item.id === menu.groupId);
      entries.push({
        ...shortcut,
        description: submenu?.translatedName || submenu?.label
          || menu.translatedName || menu.label || shortcut.description,
        groupName: group?.name || 'General',
      });
      return entries;
    }, []);
  }, [moduleConfig, translatedMenus, translations]);

  const shortcutByTarget = useMemo(() => {
    const map = new Map();
    availableShortcutEntries.forEach((shortcut) => {
      if (!shortcut.action) map.set(`${shortcut.menuId}:${shortcut.submenuId || ''}`, shortcut);
    });
    return map;
  }, [availableShortcutEntries]);

  const shortcutGroups = useMemo(() => {
    const groups = new Map();
    availableShortcutEntries.forEach((shortcut) => {
      if (!groups.has(shortcut.groupName)) groups.set(shortcut.groupName, []);
      groups.get(shortcut.groupName).push(shortcut);
    });
    return Array.from(groups, ([name, shortcuts]) => ({ name, shortcuts }));
  }, [availableShortcutEntries]);

  const runShortcutAction = useCallback((action) => {
    switch (action) {
      case 'toggleShortcuts':
        setShowShortcuts((visible) => !visible);
        setShowThemePicker(false);
        setShowShopSwitcher(false);
        setShowLanguagePicker(false);
        break;
      case 'toggleSidebar':
        setShowShortcuts(false);
        setShowThemePicker(false);
        setShowShopSwitcher(false);
        setShowLanguagePicker(false);
        if (window.innerWidth <= 768) {
          setMobileSheetOpen((visible) => !visible);
          setMobileFocusedMenu(null);
        } else {
          setSidebarCollapsed((collapsed) => !collapsed);
        }
        break;
      case 'toggleShopSwitcher':
        if (!currentShop) break;
        setShowThemePicker(false);
        setShowShortcuts(false);
        setShowLanguagePicker(false);
        setShowShopSwitcher((visible) => {
          const next = !visible;
          if (next) loadUserShops();
          return next;
        });
        break;
      case 'cycleTheme': {
        const currentIndex = THEME_PRESETS.findIndex((theme) => theme.hex.toLowerCase() === currentTheme.toLowerCase());
        const nextTheme = THEME_PRESETS[(currentIndex + 1 + THEME_PRESETS.length) % THEME_PRESETS.length];
        applyTheme(nextTheme.hex);
        setShowThemePicker(false);
        setShowShortcuts(false);
        setShowShopSwitcher(false);
        setShowLanguagePicker(false);
        break;
      }
      case 'toggleDarkMode':
        setDarkMode((isDark) => {
          const next = !isDark;
          localStorage.setItem('ourzest_darkMode', String(next));
          document.body.classList.toggle('dark-mode', next);
          return next;
        });
        setShowShortcuts(false);
        setShowThemePicker(false);
        setShowShopSwitcher(false);
        setShowLanguagePicker(false);
        break;
      case 'focusSearch':
        setShowShortcuts(false);
        setShowThemePicker(false);
        setShowShopSwitcher(false);
        setShowLanguagePicker(false);
        if (window.innerWidth <= 768) {
          setMobileSheetOpen(true);
          window.setTimeout(() => mobileSearchInputRef.current?.focus(), 100);
        } else {
          setDesktopSearchExpanded(true);
          window.setTimeout(() => desktopSearchInputRef.current?.focus(), 100);
        }
        break;
      case 'cycleLanguage': {
        const languageCodes = supportedLanguageCodes;
        const currentIndex = languageCodes.indexOf(currentLanguage);
        const nextLanguage = languageCodes[(currentIndex + 1 + languageCodes.length) % languageCodes.length];
        handleLanguageChange(nextLanguage);
        break;
      }
      default:
        break;
    }
  }, [currentTheme, currentShop, currentLanguage, applyTheme, supportedLanguageCodes]);

  const toggleSectionCollapse = useCallback((sectionId) => {
    setCollapsedSectionIds(prev =>
      prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
    );
  }, []);

  const toggleMenuExpansion = useCallback((menuId) => {
    setExpandedMenuIds((previous) => previous[0] === menuId ? [] : [menuId]);
  }, []);

  // ===== LANGUAGE HANDLER =====
  const handleLanguageChange = useCallback(async (languageCode) => {
    const safeLanguage = languageCode === 'ur' ? 'ur' : 'en';
    if (!supportedLanguageCodes.includes(safeLanguage)) {
      setShowLanguagePicker(false);
      return;
    }

    const language = LANGUAGES[safeLanguage];
    const isRtl = language.dir === 'rtl';
    
    requestAnimationFrame(() => {
      document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
      document.documentElement.lang = safeLanguage;
      document.body.style.direction = isRtl ? 'rtl' : 'ltr';
      document.documentElement.style.setProperty('--direction', isRtl ? 'rtl' : 'ltr');
      document.documentElement.classList.toggle('ourzest-rtl', isRtl);
      document.body.classList.toggle('ourzest-rtl', isRtl);
    });

    const requestId = ++languageRequestRef.current;
    const immediateTranslations = { ...translator.defaultTexts, ...(LOCAL_TRANSLATIONS[safeLanguage] || {}) };
    localStorage.setItem('ourzest_language', safeLanguage);
    localStorage.setItem('auth_language', safeLanguage);
    setTranslations(immediateTranslations);
    setCurrentLanguage(safeLanguage);
    setShowLanguagePicker(false);

    try {
      const newTranslations = await translator.getTranslations(safeLanguage);
      if (requestId === languageRequestRef.current && mountedRef.current) {
        setTranslations(newTranslations);
      }
    } catch (error) {
      console.warn('Translation load failed; using cached.', error);
    }

    const contextSetter = auth?.setLanguage || auth?.setCurrentLanguage || auth?.changeLanguage;
    if (typeof contextSetter === 'function') {
      try { await contextSetter(safeLanguage); } 
      catch (error) { console.warn('AuthContext language update failed.', error); }
    } else if (typeof setUser === 'function' && user) {
      const updatedUser = {
        ...user,
        language: safeLanguage,
        preferred_language: safeLanguage,
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }

    window.dispatchEvent(new CustomEvent('ourzest:language-change', {
      detail: { language: safeLanguage, direction: isRtl ? 'rtl' : 'ltr' },
    }));

    await loadTranslationsAndMenus();
  }, [supportedLanguageCodes, auth, setUser, user, loadTranslationsAndMenus]);

  // ===== DARK MODE TOGGLE =====
  const toggleDarkModeWithRipple = useCallback((e) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    document.documentElement.style.setProperty('--ripple-x', `${x}px`);
    document.documentElement.style.setProperty('--ripple-y', `${y}px`);

    const updateThemeState = () => {
      const nextDark = !darkMode;
      setDarkMode(nextDark);
      localStorage.setItem('ourzest_darkMode', nextDark.toString());
      document.body.classList.toggle('dark-mode', nextDark);
    };

    if (document.startViewTransition) {
      document.startViewTransition(() => {
        updateThemeState();
      });
    } else {
      updateThemeState();
    }
  }, [darkMode]);

  // ===== SHOP FUNCTIONS =====
  const loadUserShops = useCallback(async () => {
    if (!user?.id) return;
    setLoadingShops(true);
    try {
      const res = await fetch('https://ourzestapp.online/oz_backend/root_users.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_user_shop_roles', user_id: user.id })
      });
      const data = await res.json();
      if (mountedRef.current && data.success && data.data) {
        setUserShops(data.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (mountedRef.current) setLoadingShops(false);
    }
  }, [user?.id]);

  const handleShopSwitch = useCallback((shop) => {
    const updatedUser = {
      ...user,
      shop_id: shop.shop_id,
      shop_name: shop.shop_name,
      shop_code: shop.shop_code,
      role: shop.role || user.role
    };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setShowShopSwitcher(false);
    setTimeout(() => window.location.reload(), 50);
  }, [user, setUser]);

  // ===== KEYBOARD SHORTCUTS =====
  useEffect(() => {
    const handleKeyboardShortcut = (event) => {
      if (event.repeat) return;
      const shortcut = availableShortcutEntries.find((item) => (
        shortcutMatchesKeyboardEvent(item.key, event)
      ));
      if (!shortcut) return;

      const target = event.target;
      const isEditing = target instanceof HTMLElement
        && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      if (isEditing && shortcut.action !== 'toggleSidebar') return;

      event.preventDefault();
      event.stopPropagation();
      if (shortcut.action) {
        runShortcutAction(shortcut.action);
        return;
      }

      if (shortcut.submenuId) handleSubmenuClick(shortcut.menuId, shortcut.submenuId);
      else handleMenuClick(shortcut.menuId);
    };

    window.addEventListener('keydown', handleKeyboardShortcut, true);
    return () => window.removeEventListener('keydown', handleKeyboardShortcut, true);
  }, [availableShortcutEntries, handleSubmenuClick, handleMenuClick, runShortcutAction]);

  // ===== INITIALIZATION =====
  useEffect(() => {
    const initDashboard = async () => {
      setLoading(true);
      
      const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
      applyTheme(savedTheme);
      
      if (darkMode) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }

      await fetchBusinessInfo();
      const trans = await translator.getTranslations(currentLanguage);
      if (!mountedRef.current) return;
      setTranslations(trans);

      // Check for cached menus first
      const cachedMenus = getCachedMenus();
      if (cachedMenus && cachedMenus.length > 0) {
        setTranslatedMenus(cachedMenus);
        setMenuAccessLoading(false);
      }

      const DefaultDash = moduleConfig?.dashboardComponent || (() => (
        <div className="default-dashboard-view">
          <div className="welcome-banner">
            <h2>{trans.welcome}, {businessData.fullname} 👋</h2>
            <p>{trans.selectModule || 'Select a module from the navigation to begin.'}</p>
          </div>
        </div>
      ));

      setFormContent(<DefaultDash onNavigate={handleMenuNavigation} updateBreadcrumbs={updateBreadcrumbs} />);
      
      setBreadcrumbItems([
        { id: 'dashboard', label: trans.dashboard || 'Dashboard', isClickable: true }
      ]);

      setLoading(false);
    };

    if (!isInitialized) {
      initDashboard();
    }
  }, [fetchBusinessInfo, applyTheme, darkMode, currentLanguage, moduleConfig, handleMenuNavigation, businessData.fullname, isInitialized, getCachedMenus]);

  // ===== LOAD TRANSLATIONS AND MENUS ON DEPENDENCY CHANGE =====
  useEffect(() => {
    if (user?.id) {
      loadTranslationsAndMenus();
    }
  }, [currentLanguage, user?.id, user?.application_id, currentShop?.shop_id, loadTranslationsAndMenus]);

  // ===== PERIODIC REFRESH =====
  useEffect(() => {
    const reloadAccess = () => {
      if (document.visibilityState === 'visible') {
        loadTranslationsAndMenus();
      }
    };
    const accessTimer = window.setInterval(reloadAccess, 60000);
    window.addEventListener('ourzest:data-refresh', reloadAccess);
    window.addEventListener('ourzest:cache-cleared', reloadAccess);
    return () => {
      window.clearInterval(accessTimer);
      window.removeEventListener('ourzest:data-refresh', reloadAccess);
      window.removeEventListener('ourzest:cache-cleared', reloadAccess);
    };
  }, [loadTranslationsAndMenus]);

  // ===== MOUSE HANDLERS FOR FLYOUT =====
  const handleMouseEnterMenuItem = useCallback((e, menu) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);

    if (sidebarCollapsed && menu.submenus && menu.submenus.length > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const viewportMargin = 12;
      const headerHeight = 45;
      const submenuRowHeight = 42;
      const bodyPadding = 12;
      const estimatedHeight = headerHeight + bodyPadding + (menu.submenus.length * submenuRowHeight);
      const maxHeight = Math.max(180, window.innerHeight - (viewportMargin * 2));
      const renderedHeight = Math.min(estimatedHeight, maxHeight);
      const highestAllowedTop = Math.max(viewportMargin, window.innerHeight - renderedHeight - viewportMargin);
      const clampedTop = Math.min(Math.max(rect.top, viewportMargin), highestAllowedTop);

      setFlyoutPosition({ top: clampedTop, maxHeight });
      setHoveredMenu(menu);
    }
  }, [sidebarCollapsed]);

  const handleMouseLeaveMenuItem = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHoveredMenu(null);
    }, 200);
  }, []);

  const handleMouseEnterFlyout = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  const handleMouseLeaveFlyout = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHoveredMenu(null);
    }, 200);
  }, []);

  // ===== CLICK OUTSIDE =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shopSwitcherRef.current && !shopSwitcherRef.current.contains(event.target)) setShowShopSwitcher(false);
      if (themePickerRef.current && !themePickerRef.current.contains(event.target)) setShowThemePicker(false);
      if (languagePickerRef.current && !languagePickerRef.current.contains(event.target)) setShowLanguagePicker(false);
      if (shortcutPanelRef.current && !shortcutPanelRef.current.contains(event.target)) setShowShortcuts(false);
      if (desktopSearchRef.current && !desktopSearchRef.current.contains(event.target)) {
        setDesktopSearchQuery('');
        setDesktopSearchExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ===== RENDER =====
  return (
    <div className="oz-app-shell">
      <RootRuntimeBridge
        applicationId={resolvedApplicationId}
        clientVersion="business-dashboard-v3"
        onLogout={handleSecureLogout}
        loginPath={LOGIN_PATH}
      />

      <div className={`oz-dashboard-shell ${darkMode ? 'dark-mode' : 'light-mode'} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>

        {/* SIDEBAR */}
        <aside className="oz-sidebar">
          <div className="oz-sidebar-header">
            <div className="oz-brand-identity">
              <div className="oz-brand-logo-wrapper">
                {businessData.bussinesslogo ? (
                  <img src={businessData.bussinesslogo} alt="Business Logo" className="oz-brand-logo" />
                ) : (
                  <div className="oz-brand-logo-fallback">
                    <FontAwesomeIcon icon={Icons.faStore} />
                  </div>
                )}
              </div>
              {!sidebarCollapsed && (
                <div className="oz-brand-name-box">
                  <h1 className="oz-brand-name" title={businessData.businessname}>
                    {businessData.businessname}
                  </h1>
                </div>
              )}
            </div>
          </div>

          <div className="oz-sidebar-nav-scroll">
            <nav className="oz-sidebar-nav">
              {menuAccessLoading && translatedMenus.length === 0 && !sidebarCollapsed && (
                <div style={{ padding: '14px 16px', opacity: .7, fontSize: 12 }}>{translations.loadingAccess || 'Loading package access…'}</div>
              )}
              {menuAccessError && !sidebarCollapsed && (
                <div style={{ margin: '8px 12px', padding: 12, color: '#fecaca', background: 'rgba(239,68,68,.12)', borderRadius: 12, fontSize: 12 }}>
                  {menuAccessError}
                </div>
              )}
              {!menuAccessLoading && !menuAccessError && translatedMenus.length === 0 && !sidebarCollapsed && (
                <div style={{ padding: '14px 16px', opacity: .7, fontSize: 12 }}>
                  No menus are assigned by your package or administrator.
                </div>
              )}
              {groupedMenuSections.map((section) => {
                const isSectionCollapsed = collapsedSectionIds.includes(section.id);
                const showSectionHeader = section.name && section.name !== 'Main Navigation';

                return (
                  <div key={section.id} className="oz-sidebar-section">
                    {showSectionHeader && !sidebarCollapsed && (
                      <div 
                        className="oz-section-header"
                        onClick={() => toggleSectionCollapse(section.id)}
                        title="Toggle Section"
                      >
                        <span className="oz-section-title">
                          <FontAwesomeIcon icon={getIcon(section.icon)} className="oz-section-icon" />
                          {section.name}
                        </span>
                        <FontAwesomeIcon 
                          icon={isSectionCollapsed ? Icons.faChevronRight : Icons.faChevronDown} 
                          className="oz-section-chevron" 
                        />
                      </div>
                    )}

                    {(!isSectionCollapsed || sidebarCollapsed) && (
                      <div className="oz-section-menu-list">
                        {section.menus.map((menu, menuIndex) => {
                          const isActive = activeMenu === menu.id;
                          const isExpanded = expandedMenuIds.includes(menu.id);
                          const orderedSubmenus = getOrderedSubmenus(menu);
                          const hasSubmenus = orderedSubmenus.length > 0;
                          const menuShortcut = shortcutByTarget.get(`${menu.id}:`);

                          return (
                            <div 
                              key={menu.id} 
                              className={`oz-menu-item-group ${isActive ? 'active' : ''}`}
                              onMouseEnter={(e) => handleMouseEnterMenuItem(e, menu)}
                              onMouseLeave={handleMouseLeaveMenuItem}
                            >
                              <div 
                                className={`oz-menu-item ${isActive ? 'is-active' : ''}`} 
                                onClick={() => handleMenuClick(menu.id)}
                              >
                                <span className="oz-menu-icon">
                                  <FontAwesomeIcon icon={getIcon(menu.icon)} />
                                </span>
                                 {!sidebarCollapsed && (
                                   <span className="oz-menu-label">{menu.translatedName}</span>
                                 )}
                                {!sidebarCollapsed && menuShortcut && !reorderMode && (
                                  <kbd className="oz-shortcut-badge">{menuShortcut.key}</kbd>
                                )}
                                {!sidebarCollapsed && reorderMode && (
                                  <span className="oz-reorder-controls" onClick={(event) => event.stopPropagation()}>
                                    <button
                                      type="button"
                                      disabled={menuIndex === 0}
                                      onClick={() => moveNavigationItem(`group:${section.id}`, menu.id, -1, section.menus)}
                                    >
                                      <FontAwesomeIcon icon={Icons.faArrowUp} />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={menuIndex === section.menus.length - 1}
                                      onClick={() => moveNavigationItem(`group:${section.id}`, menu.id, 1, section.menus)}
                                    >
                                      <FontAwesomeIcon icon={Icons.faArrowDown} />
                                    </button>
                                  </span>
                                )}
                                {!sidebarCollapsed && hasSubmenus && (
                                  <FontAwesomeIcon 
                                    icon={isExpanded ? Icons.faChevronUp : Icons.faChevronDown} 
                                    className="oz-submenu-arrow" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMenuExpansion(menu.id);
                                    }}
                                  />
                                )}
                              </div>

                              {!sidebarCollapsed && isExpanded && hasSubmenus && (
                                <div className="oz-submenu-list">
                                  {orderedSubmenus.map((sub, submenuIndex) => {
                                    const submenuShortcut = shortcutByTarget.get(`${menu.id}:${sub.id}`);
                                    return (
                                    <div
                                      key={sub.id}
                                      className={`oz-submenu-item ${activeSubmenu === sub.id ? 'is-sub-active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSubmenuClick(menu.id, sub.id);
                                      }}
                                    >
                                      <FontAwesomeIcon icon={getIcon(sub.icon || menu.icon)} className="oz-submenu-icon" />
                                      <span className="oz-submenu-label">{sub.translatedName}</span>
                                      {submenuShortcut && !reorderMode && (
                                        <kbd className="oz-shortcut-badge oz-shortcut-badge-submenu">{submenuShortcut.key}</kbd>
                                      )}
                                      {reorderMode && (
                                        <span className="oz-reorder-controls" onClick={(event) => event.stopPropagation()}>
                                          <button
                                            type="button"
                                            disabled={submenuIndex === 0}
                                            onClick={() => moveNavigationItem(`menu:${menu.id}`, sub.id, -1, orderedSubmenus)}
                                          >
                                            <FontAwesomeIcon icon={Icons.faArrowUp} />
                                          </button>
                                          <button
                                            type="button"
                                            disabled={submenuIndex === orderedSubmenus.length - 1}
                                            onClick={() => moveNavigationItem(`menu:${menu.id}`, sub.id, 1, orderedSubmenus)}
                                          >
                                            <FontAwesomeIcon icon={Icons.faArrowDown} />
                                          </button>
                                        </span>
                                      )}
                                    </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          <div className="oz-sidebar-footer">
            {!sidebarCollapsed && (
              <div className="oz-navigation-order-actions">
                <button
                  type="button"
                  className={`oz-sidebar-reorder-btn ${reorderMode ? 'active' : ''}`}
                  onClick={() => setReorderMode((enabled) => !enabled)}
                >
                  <FontAwesomeIcon icon={Icons.faArrowDownUpAcrossLine || Icons.faSort} />
                  <span>{reorderMode ? (translations.finishOrdering || 'Finish ordering') : (translations.reorderNavigation || 'Reorder navigation')}</span>
                </button>
                {reorderMode && Object.keys(navigationOrder).length > 0 && (
                  <button
                    type="button"
                    className="oz-order-reset-btn"
                    onClick={() => {
                      localStorage.removeItem(navigationOrderStorageKey);
                      setNavigationOrder({});
                    }}
                  >
                    <FontAwesomeIcon icon={Icons.faRotateLeft} />
                  </button>
                )}
              </div>
            )}
            <button
              type="button"
              className="oz-sidebar-logout-btn"
              onClick={handleSecureLogout}
              disabled={isLoggingOut}
            >
              <FontAwesomeIcon
                icon={isLoggingOut ? Icons.faSpinner : Icons.faSignOutAlt}
                className={isLoggingOut ? 'fa-spin' : undefined}
              />
              {!sidebarCollapsed && (
                <span>{isLoggingOut ? (translations.loggingOut || 'Logging out…') : (translations.logoutThisDevice || translations.logout || 'Logout this device')}</span>
              )}
            </button>
          </div>
        </aside>

        {sidebarCollapsed && hoveredMenu && hoveredMenu.submenus && hoveredMenu.submenus.length > 0 && (
          <div 
            className="oz-sidebar-flyout" 
            style={{
              top: `${flyoutPosition.top}px`,
              maxHeight: `${flyoutPosition.maxHeight || window.innerHeight - 24}px`,
            }}
            onMouseEnter={handleMouseEnterFlyout}
            onMouseLeave={handleMouseLeaveFlyout}
          >
            <div className="oz-flyout-header">{hoveredMenu.translatedName}</div>
            <div className="oz-flyout-body">
              {getOrderedSubmenus(hoveredMenu).map((sub) => (
                <div 
                  key={sub.id} 
                  className={`oz-flyout-item ${activeSubmenu === sub.id ? 'active' : ''}`}
                  onClick={() => handleSubmenuClick(hoveredMenu.id, sub.id)}
                >
                  <FontAwesomeIcon icon={getIcon(sub.icon)} className="oz-flyout-icon" />
                  <span>{sub.translatedName}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MAIN VIEWPORT */}
        <div className="oz-main-viewport">
          
          <header className="oz-top-navbar">
            <div className="oz-nav-left">
              
              <button 
                className="oz-sidebar-toggle-btn" 
                onClick={() => {
                  if (window.innerWidth <= 768) {
                    setMobileSheetOpen(true);
                  } else {
                    setSidebarCollapsed(!sidebarCollapsed);
                  }
                }}
              >
                <FontAwesomeIcon icon={Icons.faBars} />
              </button>

              <div className="oz-breadcrumb-badge-wrapper">
                <nav className="oz-breadcrumb-trail" aria-label="breadcrumb">
                  {breadcrumbItems.map((crumb, idx) => (
                    <React.Fragment key={crumb.id || idx}>
                      {idx > 0 && <FontAwesomeIcon icon={Icons.faChevronRight} className="oz-crumb-separator" />}
                      <span 
                        className={`oz-crumb-link ${crumb.isClickable ? 'clickable' : 'active'}`}
                        onClick={() => crumb.isClickable && handleMenuClick(crumb.id)}
                      >
                        {crumb.label}
                      </span>
                    </React.Fragment>
                  ))}
                </nav>
              </div>

            </div>

            <div className="oz-nav-right">

              {/* SEARCH BUTTON & INPUT INTEGRATED IN NAV RIGHT */}
              <div className={`oz-desktop-search-container ${desktopSearchExpanded ? 'expanded' : ''}`} ref={desktopSearchRef}>
                {!desktopSearchExpanded ? (
                  <button
                    type="button"
                    className="oz-nav-pill-btn oz-search-expand-btn"
                    onClick={() => {
                      setShowShortcuts(false);
                      setShowThemePicker(false);
                      setShowShopSwitcher(false);
                      setShowLanguagePicker(false);
                      setDesktopSearchExpanded(true);
                      window.setTimeout(() => desktopSearchInputRef.current?.focus(), 100);
                    }}
                    title="Search"
                  >
                    <FontAwesomeIcon icon={Icons.faSearch} />
                  </button>
                ) : (
                  <div className="oz-search-input-box">
                    <FontAwesomeIcon icon={Icons.faSearch} className="oz-search-icon" />
                    <input
                      ref={desktopSearchInputRef}
                      type="text"
                      placeholder={translations.searchPlaceholder || "Search menus, submenus..."}
                      value={desktopSearchQuery}
                      onChange={(e) => setDesktopSearchQuery(e.target.value)}
                      className="oz-desktop-search-input"
                    />
                    <button
                      type="button"
                      className="oz-search-clear-btn"
                      onClick={() => {
                        if (desktopSearchQuery) setDesktopSearchQuery('');
                        else setDesktopSearchExpanded(false);
                      }}
                    >
                      <FontAwesomeIcon icon={Icons.faTimes} />
                    </button>
                  </div>
                )}

                {desktopSearchQuery.trim() && (
                  <div className="oz-dropdown-menu oz-search-results-menu">
                    <div className="oz-dropdown-header">{translations.searchResults || 'SEARCH RESULTS'}</div>
                    {desktopSearchResults.length === 0 ? (
                      <div className="oz-no-search-results">{translations.noMatchingMenus || 'No matching menus found'}</div>
                    ) : (
                      desktopSearchResults.map((res, i) => (
                        <div 
                          key={i} 
                          className="oz-dropdown-option oz-search-result-item"
                          onClick={() => {
                            if (res.submenuId) {
                              handleSubmenuClick(res.menuId, res.submenuId);
                            } else {
                              handleMenuClick(res.menuId);
                            }
                            setDesktopSearchQuery('');
                            setDesktopSearchExpanded(false);
                          }}
                        >
                          <div className="oz-search-res-info">
                            <FontAwesomeIcon icon={getIcon(res.icon)} className="oz-search-res-icon" />
                            <div className="oz-search-res-titles">
                              <span className="oz-search-res-main">{res.title}</span>
                              <small className="oz-search-res-parent">{res.parent}</small>
                            </div>
                          </div>
                          <FontAwesomeIcon icon={Icons.faChevronRight} className="oz-result-arrow" />
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* KEYBOARD SHORTCUTS TOGGLE */}
              <div className="oz-dropdown-container" ref={shortcutPanelRef}>
                <button
                  type="button"
                  className={`oz-nav-pill-btn oz-shortcuts-toggle-btn ${showShortcuts ? 'active' : ''}`}
                  onClick={() => {
                    setShowShortcuts((visible) => !visible);
                    setShowThemePicker(false);
                    setShowShopSwitcher(false);
                    setShowLanguagePicker(false);
                  }}
                  title="Shortcuts"
                >
                  <FontAwesomeIcon icon={Icons.faKeyboard} />
                </button>

                {showShortcuts && (
                  <div className="oz-dropdown-menu oz-shortcuts-menu">
                    <div className="oz-shortcuts-header">
                      <div>
                        <strong>{translations.keyboardShortcuts || 'Keyboard shortcuts'}</strong>
                        <small>{translations.authorizedShortcutsOnly || 'Only shortcuts you are authorized to use are shown.'}</small>
                      </div>
                      <button type="button" onClick={() => setShowShortcuts(false)}>
                        <FontAwesomeIcon icon={Icons.faTimes} />
                      </button>
                    </div>
                    <div className="oz-shortcuts-scroll">
                      {shortcutGroups.map((group) => (
                        <section key={group.name} className="oz-shortcut-group">
                          <h4>{group.name}</h4>
                          {group.shortcuts.map((shortcut) => (
                            <button
                              type="button"
                              key={`${shortcut.key}:${shortcut.menuId || shortcut.action}:${shortcut.submenuId || ''}`}
                              className="oz-shortcut-row"
                              onClick={() => {
                                if (shortcut.action) {
                                  runShortcutAction(shortcut.action);
                                } else if (shortcut.submenuId) {
                                  handleSubmenuClick(shortcut.menuId, shortcut.submenuId);
                                } else {
                                  handleMenuClick(shortcut.menuId);
                                }
                              }}
                            >
                              <span>{shortcut.description}</span>
                              <kbd>{shortcut.key}</kbd>
                            </button>
                          ))}
                        </section>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* DARK MODE TOGGLE */}
              <button 
                className="oz-nav-pill-btn oz-mode-toggle-btn" 
                onClick={(event) => {
                  toggleDarkModeWithRipple(event);
                  setShowShortcuts(false);
                  setShowThemePicker(false);
                  setShowShopSwitcher(false);
                  setShowLanguagePicker(false);
                }}
                title="Toggle Dark Mode"
              >
                <FontAwesomeIcon icon={darkMode ? Icons.faSun : Icons.faMoon} />
              </button>

              {/* THEME PICKER */}
              <div className="oz-dropdown-container" ref={themePickerRef}>
                <button 
                  className="oz-nav-pill-btn oz-theme-btn" 
                  onClick={() => {
                    setShowThemePicker((visible) => !visible);
                    setShowShortcuts(false);
                    setShowShopSwitcher(false);
                    setShowLanguagePicker(false);
                  }}
                  title="Theme Color"
                >
                  <FontAwesomeIcon icon={Icons.faPalette} style={{ color: currentTheme }} />
                </button>

                {showThemePicker && (
                  <div className="oz-dropdown-menu oz-theme-menu">
                    <div className="oz-dropdown-header">{translations.themeColor || 'THEME COLOR'}</div>
                    <div className="oz-theme-options-grid">
                      {THEME_PRESETS.map((t) => (
                        <button
                          key={t.hex}
                          className={`oz-theme-color-swatch ${currentTheme === t.hex ? 'active' : ''}`}
                          style={{ backgroundColor: t.hex }}
                          onClick={() => { applyTheme(t.hex); setShowThemePicker(false); }}
                          title={t.name}
                        >
                          {currentTheme === t.hex && <FontAwesomeIcon icon={Icons.faCheck} />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* LANGUAGE PICKER */}
              <div className="oz-dropdown-container" ref={languagePickerRef}>
                <button
                  type="button"
                  className={`oz-nav-pill-btn oz-language-btn ${showLanguagePicker ? 'active' : ''}`}
                  onClick={() => {
                    setShowLanguagePicker((visible) => !visible);
                    setShowThemePicker(false);
                    setShowShortcuts(false);
                    setShowShopSwitcher(false);
                  }}
                  title="Language"
                >
                  <FontAwesomeIcon icon={Icons.faLanguage || Icons.faGlobe} />
                </button>

                {showLanguagePicker && (
                  <div className="oz-dropdown-menu oz-language-menu">
                    <div className="oz-dropdown-header">{translations.language || 'LANGUAGE'}</div>
                    {Object.entries(LANGUAGES).filter(([code]) => supportedLanguageCodes.includes(code)).map(([code, language]) => (
                      <button
                        type="button"
                        key={code}
                        className={`oz-dropdown-option oz-language-option ${currentLanguage === code ? 'selected' : ''}`}
                        onClick={() => handleLanguageChange(code)}
                      >
                        <span className="oz-language-name">
                          <span>{language.icon}</span>
                          <span>
                            <strong>{language.name}</strong>
                            <small>{language.nativeName}</small>
                          </span>
                        </span>
                        {currentLanguage === code && <FontAwesomeIcon icon={Icons.faCheck} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* SHOP SWITCHER */}
              {currentShop && (
                <div className="oz-dropdown-container oz-desktop-shop-container" ref={shopSwitcherRef}>
                  <button className="oz-shop-switch-pill" onClick={() => {
                    setShowShopSwitcher((visible) => !visible);
                    setShowThemePicker(false);
                    setShowShortcuts(false);
                    setShowLanguagePicker(false);
                    loadUserShops();
                  }} title="Switch Shop">
                    <FontAwesomeIcon icon={Icons.faStore} className="oz-shop-pill-icon" />
                  </button>

                  {showShopSwitcher && (
                    <div className="oz-dropdown-menu oz-shop-menu">
                      <div className="oz-dropdown-header">{translations.switchShops || 'SWITCH SHOP'}</div>
                      {loadingShops ? (
                        <div className="p-3 text-center text-muted">{translations.loadingShops || 'Loading shops...'}</div>
                      ) : (
                        userShops.map(shop => (
                          <div 
                            key={shop.shop_id} 
                            className={`oz-dropdown-option ${shop.shop_id === currentShop.shop_id ? 'selected' : ''}`}
                            onClick={() => handleShopSwitch(shop)}
                          >
                            <div className="d-flex flex-column">
                              <span className="fw-bold">{shop.shop_name}</span>
                            </div>
                            {shop.shop_id === currentShop.shop_id && <FontAwesomeIcon icon={Icons.faCheck} />}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* USER AVATAR */}
              <div className="oz-user-profile-display" title={businessData.fullname}>
                {businessData.profile_image ? (
                  <img src={businessData.profile_image} alt="User Profile" className="oz-user-avatar-img" />
                ) : (
                  <div className="oz-user-avatar-initials">
                    {getInitials(businessData.fullname)}
                  </div>
                )}
                <span className="oz-online-status-dot" title="Online"></span>
              </div>

            </div>
          </header>

          <main className="oz-content-area">
            <Suspense fallback={<div className="oz-inline-pending" role="status" aria-label="Opening module" />}>
              {formContent}
            </Suspense>
          </main>
        </div>

        {mobileSheetOpen && (
          <div className="oz-mobile-sheet-overlay" onClick={() => { setMobileSheetOpen(false); setMobileFocusedMenu(null); setMobileSearchQuery(''); }}>
            <div className="oz-mobile-bottom-sheet" onClick={(e) => e.stopPropagation()}>
              <div className="oz-mobile-sheet-handle"></div>
              
              <div className="oz-mobile-sheet-header">
                <h3>{translations.navigationMenu || 'Navigation Menu'}</h3>
                <button className="oz-mobile-sheet-close" onClick={() => { setMobileSheetOpen(false); setMobileFocusedMenu(null); setMobileSearchQuery(''); }}>
                  <FontAwesomeIcon icon={Icons.faTimes} />
                </button>
              </div>

              <div className="oz-mobile-sheet-body">
                <div className="oz-mobile-search-box">
                  <FontAwesomeIcon icon={Icons.faSearch} className="oz-mobile-search-icon" />
                  <input 
                    ref={mobileSearchInputRef}
                    type="text" 
                    placeholder={translations.searchPlaceholder || 'Search menus & submenus...'}
                    value={mobileSearchQuery}
                    onChange={(e) => setMobileSearchQuery(e.target.value)}
                    className="oz-mobile-search-input"
                  />
                  {mobileSearchQuery && (
                    <button className="oz-mobile-search-clear" onClick={() => setMobileSearchQuery('')}>
                      <FontAwesomeIcon icon={Icons.faTimes} />
                    </button>
                  )}
                </div>

                {mobileSearchQuery.trim() ? (
                  <div className="oz-mobile-search-results-list">
                    {mobileSearchResults.length === 0 ? (
                      <div className="p-3 text-center text-muted">{translations.noMatchingMenus || 'No matching menus found'}</div>
                    ) : (
                      mobileSearchResults.map((res, i) => (
                        <div 
                          key={i} 
                          className="oz-mobile-search-result-card"
                          onClick={() => {
                            if (res.submenuId) {
                              handleSubmenuClick(res.menuId, res.submenuId);
                            } else {
                              handleMenuClick(res.menuId);
                            }
                            setMobileSearchQuery('');
                          }}
                        >
                          <FontAwesomeIcon icon={getIcon(res.icon)} className="oz-mobile-res-icon" />
                          <div className="oz-mobile-res-text">
                            <span className="oz-mobile-res-title">{res.title}</span>
                            <small className="oz-mobile-res-parent">{res.parent}</small>
                          </div>
                          <FontAwesomeIcon icon={Icons.faChevronRight} className="oz-mobile-res-arrow" />
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  groupedMenuSections.map((section) => (
                    <div key={section.id} className="oz-mobile-section-block">
                      {section.name && section.name !== 'Main Navigation' && (
                        <div className="oz-mobile-section-title">
                          <FontAwesomeIcon icon={getIcon(section.icon)} />
                          {section.name}
                        </div>
                      )}

                      {section.menus.map((menu) => {
                        const isFocused = mobileFocusedMenu === menu.id;
                        const isOtherBlurred = mobileFocusedMenu !== null && !isFocused;
                        const orderedSubmenus = getOrderedSubmenus(menu);
                        const hasSubmenus = orderedSubmenus.length > 0;

                        return (
                          <div 
                            key={menu.id} 
                            className={`oz-mobile-menu-card ${isFocused ? 'focused' : ''} ${isOtherBlurred ? 'blurred' : ''}`}
                            onClick={() => {
                              if (hasSubmenus) {
                                setMobileFocusedMenu(isFocused ? null : menu.id);
                              } else {
                                handleMenuClick(menu.id);
                              }
                            }}
                          >
                            <div className="oz-mobile-menu-row">
                              <span className="oz-mobile-menu-icon">
                                <FontAwesomeIcon icon={getIcon(menu.icon)} />
                              </span>
                              <span className="oz-mobile-menu-title">{menu.translatedName}</span>
                              {hasSubmenus && (
                                <FontAwesomeIcon 
                                  icon={isFocused ? Icons.faChevronUp : Icons.faChevronDown} 
                                  className="oz-mobile-arrow" 
                                />
                              )}
                            </div>

                            {hasSubmenus && isFocused && (
                              <div className="oz-mobile-submenus-container">
                                {orderedSubmenus.map((sub) => (
                                  <div 
                                    key={sub.id} 
                                    className="oz-mobile-submenu-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSubmenuClick(menu.id, sub.id);
                                    }}
                                  >
                                     <FontAwesomeIcon icon={getIcon(sub.icon)} />
                                     <span>{sub.translatedName}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>

              <div
                className="oz-mobile-sheet-footer"
                style={{
                  flexShrink: 0,
                  paddingTop: 12,
                  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                  borderTop: '1px solid var(--border-color)'
                }}
              >
                <button
                  type="button"
                  className="oz-sidebar-logout-btn oz-mobile-logout-btn"
                  onClick={handleSecureLogout}
                  disabled={isLoggingOut}
                  style={{
                    width: '100%',
                    minHeight: 46,
                    justifyContent: 'center',
                    opacity: isLoggingOut ? 0.68 : 1,
                    cursor: isLoggingOut ? 'wait' : 'pointer'
                  }}
                >
                  <FontAwesomeIcon
                    icon={isLoggingOut ? Icons.faSpinner : Icons.faSignOutAlt}
                    className={isLoggingOut ? 'fa-spin' : undefined}
                  />
                  <span>
                    {isLoggingOut
                      ? (translations.loggingOut || 'Logging out…')
                      : (translations.logoutThisDevice || translations.logout || 'Logout this device')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MOBILE FLOATING PILL BOTTOM NAVIGATION DOCK */}
        <nav className="oz-mobile-bottom-dock">
          <button 
            className={`oz-dock-item ${activeMenu === 'dashboard' && !mobileSheetOpen ? 'active' : ''}`}
            onClick={() => { handleMenuClick('dashboard'); setMobileSheetOpen(false); }}
          >
            <FontAwesomeIcon icon={Icons.faHome} className="oz-dock-icon" />
            <span className="oz-dock-text">{translations.dashboard || 'Dashboard'}</span>
          </button>

          {mobileQuickMenus.map((menu) => (
            <button
              key={menu.id}
              className={`oz-dock-item ${activeMenu === menu.id && !mobileSheetOpen ? 'active' : ''}`}
              onClick={() => {
                handleMenuClick(menu.id);
                setMobileSheetOpen(false);
              }}
            >
              <FontAwesomeIcon icon={getIcon(menu.icon)} className="oz-dock-icon" />
              <span className="oz-dock-text">{menu.translatedName || menu.label}</span>
            </button>
          ))}

          <button 
            className={`oz-dock-item ${mobileSheetOpen ? 'active' : ''}`}
            onClick={() => setMobileSheetOpen(!mobileSheetOpen)}
          >
            <FontAwesomeIcon icon={Icons.faEllipsisH} className="oz-dock-icon" />
            <span className="oz-dock-text">{translations.menu || 'Menu'}</span>
          </button>

          <div className="oz-dock-home-indicator"></div>
        </nav>

      </div>
    </div>
  );
};

// Export with React.memo to prevent unnecessary re-renders
export default React.memo(DashboardBusinessManager);